import { useCallback, useEffect, useRef, useState } from "react";

import { THeading, TSlide } from "../../../types/index";
import BaseHeadline from "../../../components/general/BaseHeadline";
import { API } from "../../../api/api";

type IndustryServicesType = {
    heading: THeading;
    slides: TSlide[];
};

export default function IndustryServices(props: IndustryServicesType) {
    const sliderRef = useRef<HTMLDivElement | null>(null);
    const [canScrollPrev, setCanScrollPrev] = useState(false);
    const [canScrollNext, setCanScrollNext] = useState(true);
    const sliderSideInset = "max(0px, calc((100vw - 80rem) / 2))";
    const sliderRightInset = "max(1.5rem, calc((100vw - 80rem) / 2 + 1.5rem))";

    const getNavButtonClass = (isEnabled: boolean) =>
        `inline-flex h-12 w-12 items-center justify-center rounded-full border text-xl transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 ${
            isEnabled
                ? "border-primary/50 text-primary bg-primary/5 hover:bg-primary/15 hover:border-primary/70 active:scale-95"
                : "cursor-not-allowed border-base-300/40 text-base-300/70 "
        }`;

    const updateScrollState = useCallback(() => {
        const slider = sliderRef.current;
        if (!slider) return;

        const maxScroll = slider.scrollWidth - slider.clientWidth;
        setCanScrollPrev(slider.scrollLeft > 4);
        setCanScrollNext(slider.scrollLeft < maxScroll - 4);
    }, []);

    useEffect(() => {
        const slider = sliderRef.current;
        if (!slider) return;

        updateScrollState();

        const handleScroll = () => updateScrollState();
        slider.addEventListener("scroll", handleScroll, { passive: true });
        window.addEventListener("resize", handleScroll);

        return () => {
            slider.removeEventListener("scroll", handleScroll);
            window.removeEventListener("resize", handleScroll);
        };
    }, [updateScrollState]);

    const scrollBySlide = (direction: 1 | -1) => {
        const slider = sliderRef.current;
        if (!slider) return;

        const firstSlide = slider.querySelector('[data-slide="area-slide"]') as HTMLElement | null;
        const slideWidth = firstSlide?.offsetWidth ?? slider.clientWidth * 0.82;
        const gap = 24;

        slider.scrollBy({
            left: direction * (slideWidth + gap),
            behavior: "smooth",
        });
    };

    return (
        <section className="py-20 md:py-28 overflow-x-hidden">
            <div className="px-6 md:px-12 mb-8 md:mb-10">
                <div className="max-w-7xl mx-auto flex items-end justify-between gap-6">
                    <BaseHeadline label={props.heading.label} headline={props.heading.headline} />

                    <div className="flex items-center gap-3 pb-4 shrink-0">
                        <button
                            onClick={() => scrollBySlide(-1)}
                            disabled={!canScrollPrev}
                            aria-label="Vorheriger Slide"
                            className={getNavButtonClass(canScrollPrev)}
                        >
                            ←
                        </button>
                        <button
                            onClick={() => scrollBySlide(1)}
                            disabled={!canScrollNext}
                            className={getNavButtonClass(canScrollNext)}
                            aria-label="Nächster Slide"
                        >
                            →
                        </button>
                    </div>
                </div>
            </div>

            <div
                ref={sliderRef}
                className="w-screen overflow-x-auto scroll-smooth [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
            >
                <div
                    className="flex w-max items-start"
                    style={{ paddingLeft: sliderSideInset, paddingRight: sliderRightInset }}
                >

                    {props.slides.map((slide, index) => (
                        <article
                            key={slide.headline}
                            data-slide="area-slide"
                            className={`shrink-0 w-[72vw] sm:w-[60vw] lg:w-[40vw] xl:w-[30vw] pl-6 md:pl-12 xl:pl-0 ${index > 0 ? "xl:ml-6" : ""}`}
                        >
                            <div className="h-72 md:h-96 overflow-hidden">
                                <img
                                    src={API.img + slide.image.data.attributes.url}
                                    alt={slide.headline}
                                    className="h-full w-full object-cover"
                                    loading="lazy"
                                />
                            </div>

                            <div className="pt-5 md:pt-6">
                                <BaseHeadline
                                    headline={slide.headline}
                                    text={slide.description}
                                    classProps="max-w-2xl"
                                    tag="h4"
                                    headlineClassProps="uppercase"
                                />
                            </div>
                        </article>
                    ))}
                   
                </div>
            </div>
        </section>
    );
}
